"use client";

import { useEffect } from "react";
import { CONSENT_CHANGED_EVENT } from "@/lib/analytics/consent";
import { newReadMarks, readFraction, trackEvent, type ContentView } from "@/lib/analytics/events";

/**
 * A detail page's analytics (lib/analytics/events.ts): `content_view` once,
 * and on a reading page (`reading`) the read depth through the
 * `data-reader-text` region plus, for paid content, clicks on its
 * `rel="sponsored"` links. Renders nothing.
 *
 * The view counts the page the visitor is on even when consent arrives after
 * it opened — the banner's accept sends it then, as GA sends its own first
 * page view. Nothing is sent without consent.
 */
export function ContentAnalytics({
  view,
  reading = false,
}: {
  view: ContentView;
  reading?: boolean;
}) {
  const key = JSON.stringify(view);

  useEffect(() => {
    const v = JSON.parse(key) as ContentView;
    const ref = {
      content_type: v.content_type,
      content_id: v.content_id,
      sponsored: v.sponsored,
      sponsor_name: v.sponsor_name,
    };

    let viewed = false;
    const sendView = () => {
      if (!viewed) viewed = trackEvent("content_view", v);
    };
    sendView();
    window.addEventListener(CONSENT_CHANGED_EVENT, sendView);
    if (!reading) return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sendView);

    const region = document.querySelector<HTMLElement>("[data-reader-text]");
    let sent = 0;
    let raf = 0;
    const measure = () => {
      raf = 0;
      if (!region) return;
      const r = region.getBoundingClientRect();
      for (const percent of newReadMarks(readFraction(r.top, r.height, window.innerHeight), sent)) {
        if (!trackEvent("read_progress", { ...ref, percent })) return;
        sent = percent;
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const onClick = (e: MouseEvent) => {
      if (v.sponsored === "none") return;
      const a =
        e.target instanceof Element
          ? e.target.closest<HTMLAnchorElement>('[data-reader-text] a[rel~="sponsored"]')
          : null;
      if (!a) return;
      let domain = "";
      try {
        domain = new URL(a.href).hostname;
      } catch {
        return;
      }
      trackEvent("sponsored_link_click", { ...ref, link_url: a.href, link_domain: domain });
    };
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, sendView);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick);
      cancelAnimationFrame(raf);
    };
  }, [key, reading]);

  return null;
}
